import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Scale, Receipt, ShieldCheck, Calculator, BookOpen, ArrowRight } from "lucide-react";

const VIDEO_API = `${process.env.REACT_APP_BACKEND_URL}/api/static/videos`;

/**
 * Accounting & Fee Breakdown. Explains the 1.5% trustee settlement fee from
 * an accounting standpoint — what it covers, when it's collected, and the
 * good accounting ledger rationale.
 */
const LINE_ITEMS = [
  { label: "Good accounting ledger audit", pct: 0.35, desc: "Forensic line-by-line audit of the mortgage, taxes, and lender accounting record." },
  { label: "Trustee pledge & escrow", pct: 0.30, desc: "Guild pledges funds into the trustee account to trigger mortgage reassignment." },
  { label: "Counterclaim filing & service", pct: 0.20, desc: "Court filing fees, certified service to the law firm and bank, court docket fees." },
  { label: "Mediation & arbitration", pct: 0.10, desc: "Mediation scheduling, arbitrator retainer, transcripts, and binding award filing." },
  { label: "Settlement recording & trustee oversight", pct: 0.05, desc: "Final mortgage reassignment recording, ledger close-out, member portal updates." },
];

function Row({ label, value, accent }) {
  return (
    <div className={`flex items-center justify-between py-3 border-b border-black/5 ${accent ? "border-t-2 border-t-[color:var(--fire-red)] font-medium" : ""}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

export default function Accounting() {
  useDocumentTitle(
    "Accounting & Fee Breakdown — Azure Gypsy Guild 1.5% Settlement Fee",
    "Line-by-line accounting explanation of the Azure Gypsy Guild 1.5% counterclaim service fee: what it covers, when it's paid, and the good accounting ledger rationale behind it.",
  );

  // Worked example: $250,000 mortgage.
  const exampleBalance = 250000;
  const exampleFee = exampleBalance * 0.015;

  return (
    <div className="min-h-screen bg-paper" data-testid="accounting-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-16">
        <div className="max-w-3xl">
          <span className="uppercase text-xs tracking-[0.25em] text-[color:var(--fire-red)]" data-testid="accounting-eyebrow">
            Accounting & Settlement Fee
          </span>
          <h1 className="font-serif text-4xl sm:text-6xl mt-3 leading-none">
            Why 1.5% — explained from an accounting standpoint.
          </h1>
          <p className="text-[color:var(--text-secondary)] mt-6 text-lg leading-relaxed">
            Azure Gypsy Guild charges a single counterclaim service fee equal to <strong>1.5% of your mortgage
            balance</strong>, payable <strong>only at settlement</strong>. This page walks through the line items
            that fee covers, when it is recognized on the trustee ledger, and the accounting principles that
            justify the percentage.
          </p>
        </div>

        {/* Snapshot */}
        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          <Card className="border-black/5" data-testid="accounting-card-rate">
            <CardContent className="p-6">
              <Scale className="h-5 w-5 text-[color:var(--orange)]" />
              <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mt-3">Rate</div>
              <div className="font-serif text-4xl mt-1">1.5%</div>
              <p className="text-xs text-[color:var(--text-secondary)] mt-2">of mortgage balance on file at intake.</p>
            </CardContent>
          </Card>
          <Card className="border-black/5" data-testid="accounting-card-when">
            <CardContent className="p-6">
              <Receipt className="h-5 w-5 text-[color:var(--bright-green)]" />
              <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mt-3">Recognized</div>
              <div className="font-serif text-2xl mt-1 leading-tight">Only at settlement</div>
              <p className="text-xs text-[color:var(--text-secondary)] mt-2">Nothing owed during the 60-day process — the Guild funds the work up front.</p>
            </CardContent>
          </Card>
          <Card className="border-black/5" data-testid="accounting-card-frequency">
            <CardContent className="p-6">
              <ShieldCheck className="h-5 w-5 text-[color:var(--fire-red)]" />
              <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mt-3">Frequency</div>
              <div className="font-serif text-2xl mt-1 leading-tight">Once per counterclaim</div>
              <p className="text-xs text-[color:var(--text-secondary)] mt-2">No monthly billing, no markup at completion.</p>
            </CardContent>
          </Card>
        </div>

        {/* Line items */}
        <section className="mt-16">
          <h2 className="font-serif text-3xl">What the 1.5% covers — line by line</h2>
          <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
            Each percentage point of the 1.5% fee is allocated to a specific accounting cost center on the
            trustee ledger. Allocations below are the Guild&apos;s standard internal cost basis.
          </p>

          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="accounting-breakdown-table">
              <thead className="bg-[color:var(--bg-secondary)] text-left text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                <tr>
                  <th className="p-4">Cost center</th>
                  <th className="p-4 w-20">Share</th>
                  <th className="p-4">Description</th>
                </tr>
              </thead>
              <tbody>
                {LINE_ITEMS.map((item) => (
                  <tr key={item.label} className="border-t border-black/5" data-testid={`accounting-row-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <td className="p-4 font-medium">{item.label}</td>
                    <td className="p-4 font-mono">{(item.pct * 100).toFixed(0)}%</td>
                    <td className="p-4 text-[color:var(--text-secondary)]">{item.desc}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-t-[color:var(--fire-red)] bg-[color:var(--fire-red)]/5 font-medium">
                  <td className="p-4">Total of 1.5% counterclaim service fee</td>
                  <td className="p-4 font-mono">100%</td>
                  <td className="p-4 text-[color:var(--text-secondary)]">Single line entry on the trustee settlement statement.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Worked example */}
        <section className="mt-16 grid lg:grid-cols-2 gap-10">
          <Card className="border-black/5">
            <CardContent className="p-8">
              <Calculator className="h-5 w-5 text-[color:var(--orange)]" />
              <h2 className="font-serif text-2xl mt-3">Worked example — a $250,000 mortgage</h2>
              <p className="text-[color:var(--text-secondary)] text-sm mt-2">
                What you actually pay, lifecycle: intake → settlement.
              </p>
              <div className="mt-5 text-sm" data-testid="accounting-worked-example">
                <Row label="Mortgage balance on file" value={`$${exampleBalance.toLocaleString()}`} />
                <Row label="Yearly Guild membership (paid up front)" value="$1,500.00" />
                <Row label="Counterclaim service fee (1.5% × $250,000)" value={`$${exampleFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                <Row label="Paid during 60-day process" value="$0.00" />
                <Row label="Total at settlement" value={`$${(1500 + exampleFee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} accent />
              </div>
              <p className="text-[10px] text-[color:var(--text-secondary)] mt-4 leading-relaxed">
                Note: the $1,500 membership is collected on enrollment via Stripe. The 1.5% counterclaim fee
                is invoiced and collected only at the close of settlement, alongside mortgage reassignment.
              </p>
            </CardContent>
          </Card>

          <Card className="border-black/5">
            <CardContent className="p-8">
              <BookOpen className="h-5 w-5 text-[color:var(--bright-green)]" />
              <h2 className="font-serif text-2xl mt-3">Accounting principles applied</h2>
              <ul className="mt-5 space-y-4 text-sm text-[color:var(--text-secondary)]" data-testid="accounting-principles">
                <li>
                  <strong className="text-[color:var(--text-primary)]">Matching principle.</strong>{" "}
                  Fee is recognized only when revenue (the settlement) materializes — costs and revenue
                  are matched on the same period close.
                </li>
                <li>
                  <strong className="text-[color:var(--text-primary)]">Trustee fund accounting.</strong>{" "}
                  The Guild funds audit, filing, mediation, and pledge costs out of trustee reserves
                  during the 60-day process — the homeowner is not invoiced for work-in-progress.
                </li>
                <li>
                  <strong className="text-[color:var(--text-primary)]">Proportionality to subject value.</strong>{" "}
                  Tying the fee to the mortgage balance keeps the engagement proportional to the value
                  being preserved, in line with private-trust fee conventions.
                </li>
                <li>
                  <strong className="text-[color:var(--text-primary)]">Single line entry.</strong>{" "}
                  The full 1.5% appears as one line on the settlement statement — no hidden surcharges,
                  no monthly recurring fees, no markup at completion.
                </li>
                <li>
                  <strong className="text-[color:var(--text-primary)]">Outcome alignment.</strong>{" "}
                  Because the fee is paid only at settlement, the Guild&apos;s incentives are aligned with
                  the homeowner: no settlement, no fee.
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* AI-generated trustee testimonials (placeholder until real members record their own) */}
        <section className="mt-16" data-testid="accounting-videos-section">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-serif text-3xl">Trustee testimonials</h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
              AI-generated placeholders — real member videos coming soon
            </span>
          </div>
          <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
            Short walkthroughs explaining the 1.5% line items. These are interim AI-generated clips while
            the Guild collects real member testimonials.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            <figure className="rounded-lg overflow-hidden border border-black/5 bg-black" data-testid="accounting-video-trustee">
              <video
                controls
                preload="metadata"
                playsInline
                className="w-full aspect-video"
                poster=""
              >
                <source src={`${VIDEO_API}/accounting-trustee-1`} type="video/mp4" />
                Your browser does not support video playback.
              </video>
              <figcaption className="px-4 py-3 text-xs text-[color:var(--text-secondary)] bg-white border-t border-black/5">
                Trustee walkthrough — &ldquo;The 1.5% counterclaim fee is a single line on your settlement statement.&rdquo;
              </figcaption>
            </figure>
            <figure className="rounded-lg overflow-hidden border border-black/5 bg-black" data-testid="accounting-video-cpa">
              <video
                controls
                preload="metadata"
                playsInline
                className="w-full aspect-video"
                poster=""
              >
                <source src={`${VIDEO_API}/accounting-cpa-1`} type="video/mp4" />
                Your browser does not support video playback.
              </video>
              <figcaption className="px-4 py-3 text-xs text-[color:var(--text-secondary)] bg-white border-t border-black/5">
                CPA worked example — &ldquo;On a $250,000 mortgage, that&apos;s $3,750, paid only at settlement.&rdquo;
              </figcaption>
            </figure>
          </div>
        </section>

        {/* Comparison */}
        <section className="mt-16">
          <h2 className="font-serif text-3xl">How that compares</h2>
          <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
            Foreclosure defense law firms typically charge a retainer plus hourly billing or a percentage
            of equity preserved. The Guild&apos;s 1.5% flat is materially lower than market rates for the
            equivalent scope and outcome.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {[
              { label: "Foreclosure defense attorney", value: "5-15% + retainer", note: "Hourly billing accrues during litigation." },
              { label: "Loan modification consultant", value: "3-6%", note: "Often not refundable if modification fails." },
              { label: "Azure Gypsy Guild", value: "1.5%", note: "Single line, at settlement, no settlement = no fee.", highlight: true },
            ].map((c) => (
              <Card key={c.label} className={`border-black/5 ${c.highlight ? "border-[color:var(--fire-red)]/40 bg-[color:var(--fire-red)]/5" : ""}`} data-testid={`accounting-compare-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <CardContent className="p-6">
                  <Badge variant="outline" className="border-black/10 text-[10px] uppercase tracking-[0.2em]">{c.label}</Badge>
                  <div className="font-serif text-3xl mt-3">{c.value}</div>
                  <p className="text-xs text-[color:var(--text-secondary)] mt-2">{c.note}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="mt-16 border-t border-black/10 pt-8 flex flex-wrap items-center justify-between gap-4">
          <a href="/pricing" className="text-[color:var(--fire-red)] inline-flex items-center gap-2 font-medium" data-testid="accounting-to-pricing">
            See pricing cards <ArrowRight className="h-4 w-4" />
          </a>
          <a href="/apply" className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white px-6 py-3 rounded-md inline-flex items-center gap-2 font-medium" data-testid="accounting-to-apply">
            Start your 60-day counterclaim <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <p className="text-[10px] text-[color:var(--text-secondary)] mt-8 leading-relaxed max-w-3xl">
          Accounting allocations on this page reflect the Guild&apos;s standard internal cost basis and may
          vary by case complexity. The 1.5% headline rate, however, does not vary by case and is locked
          at intake. Nothing on this page constitutes legal, tax, or financial advice.
        </p>
      </main>
      <Footer />
    </div>
  );
}
